import {
  BufferGeometry,
  Float32BufferAttribute,
  LinearFilter,
  Mesh,
  NearestFilter,
  NearestMipMapLinearFilter,
  OrthographicCamera,
  Vector2,
  WebGLRenderTarget,
} from 'three';

import { appMixin } from '../utils/appMixin';

import { DEVICE } from '../utils/config';
import { PostProcessingMaterial } from './Materials/PostProcessing/material';
import { PostEmissiveMaterial } from './Materials/PostEmissive/material';

export class PostProcessing extends appMixin() {
  constructor(app) {
    super(app);
    this.diffuseRenderTarget = this._createRenderTarget();
    this.diffuseRenderTarget.texture.magFilter = this.diffuseRenderTarget.texture.minFilter =
      LinearFilter;
    this.emissiveDpr = 0.8;

    this.defaultEmissiveIntensity = 0.5;

    this.emissiveRenderTarget1 = this._createEmissiveRenderTarget();
    this.emissiveRenderTarget2 = this._createEmissiveRenderTarget();
    this.emissiveRenderTarget3 = this._createEmissiveRenderTarget();

    this._material = new PostProcessingMaterial({
      defines: {
        USE_FXAA: !DEVICE.isWebgl2,
      },
      uniforms: {
        ...this.app.uniforms,

        // Textures
        tDiffuse: { value: this.diffuseRenderTarget.texture },
        tEmissive: { value: this.emissiveRenderTarget3.texture },
        tNoise: { value: this.app.tools.noise.linearTexture },

        // Viewport
        uResolution: { value: new Vector2() },
        uEmissiveResolution: { value: new Vector2() },
        uRatio: { value: this.app.viewport.ratio },

        uEmissiveRadius: { value: 4 },
        uEmissiveIntensity: { value: this.defaultEmissiveIntensity },
        uEmissiveOnly: { value: 0 },
      },
    });

    this._emissiveMaterial = new PostEmissiveMaterial({
      defines: {
        USE_FXAA: !DEVICE.isWebgl2,
      },
      uniforms: {
        ...this.app.uniforms,

        // Textures
        tEmissive: { value: this.emissiveRenderTarget1.texture },
        tNoise: { value: this.app.tools.noise.texture },

        uSpeed: { value: 0 },
        uHorizontal: { value: 0 },
        // Viewport
        uResolution: { value: new Vector2() },
        uRatio: { value: this.app.viewport.ratio },
      },
    });

    const geo = new BufferGeometry()
      .setAttribute('position', new Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3))
      .setAttribute('uv', new Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2));

    this.quad = new Mesh(geo, this._material);
    this.emissiveQuad = new Mesh(geo, this._emissiveMaterial);
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  }

  _createRenderTarget() {
    const renderTarget = new WebGLRenderTarget(
      this.app.viewport.width * this.app.viewport.dpr,
      this.app.viewport.height * this.app.viewport.dpr,
      {
        samples: 0,
      }
    );

    return renderTarget;
  }

  _createEmissiveRenderTarget() {
    const renderTarget = new WebGLRenderTarget(
      this.app.viewport.width * this.emissiveDpr,
      this.app.viewport.height * this.emissiveDpr,
      {
        samples: 0,
      }
    );

    return renderTarget;
  }

  onResize({ width, height, dpr, ratio }) {
    this._material.uniforms.uResolution.value.set(width * dpr, height * dpr);
    this._material.uniforms.uEmissiveResolution.value.set(
      width * this.emissiveDpr,
      height * this.emissiveDpr
    );
    this._emissiveMaterial.uniforms.uResolution.value.set(
      width * this.emissiveDpr,
      height * this.emissiveDpr
    );
    this._material.uniforms.uRatio.value = ratio;

    this.diffuseRenderTarget.setSize(width * dpr, height * dpr);
    this.emissiveRenderTarget1.setSize(width * this.emissiveDpr, height * this.emissiveDpr);
    this.emissiveRenderTarget2.setSize(width * this.emissiveDpr, height * this.emissiveDpr);
    this.emissiveRenderTarget3.setSize(width * this.emissiveDpr, height * this.emissiveDpr);
  }

  get uniforms() {
    return this._material.uniforms;
  }

  get emissiveUniforms() {
    return this._emissiveMaterial.uniforms;
  }
}
